import { useState, useEffect, useCallback } from 'react'

interface DeviceInfo {
  isLandscape: boolean
  isMobile: boolean
  isTablet: boolean
  isTouch: boolean
}

export const getIsLandscape = (isMobile: boolean) => {
  const isInnerLandscape = window.innerWidth > window.innerHeight

  const inIframe = window !== window.parent
  // screen.orientation is missing on iOS Safari < 16.4 and some older WKWebViews
  const screenOrientationType = window.screen?.orientation?.type || ''
  const screenSaysLandscape = screenOrientationType.includes('landscape')

  // If we're in DevTools in an iframe, screen.orientation is unreliable
  const orientationMismatch = inIframe && isMobile && screenSaysLandscape && !isInnerLandscape

  if (orientationMismatch) {
    return false // Treat as portrait
  }

  // Real device or non-emulated environment: trust screen.orientation if present
  if (screenOrientationType) {
    return screenSaysLandscape
  }

  // fallback for iOS Safari 15 and older
  if (typeof window.orientation === 'number') {
    return window.orientation === 90 || window.orientation === -90
  }

  // Final fallback
  return isInnerLandscape
}

export const useDeviceInfo = (): DeviceInfo => {
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo>({
    isLandscape: false,
    isMobile: false,
    isTablet: false,
    isTouch: false
  })

  const checkDevice = useCallback(() => {
    // Touch capability detection
    const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0

    // User agent checks
    const userAgent = navigator.userAgent
    const isMobileDevice = /Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      userAgent
    )
    const isTabletDevice =
      /iPad|tablet|Nexus(?!.*Mobile)/i.test(userAgent) ||
      (navigator.platform === 'MacIntel' &&
        navigator.maxTouchPoints > 1 &&
        !/iPhone|iPod/.test(userAgent))

    // Foldable detection (early APIs)
    const isFoldable = 'screen' in window && 'fold' in (window.screen as any)
    const isUnfolded = isFoldable && (window.screen as any).fold?.posture === 'expanded'

    // Orientation check
    const isLandscape = getIsLandscape(isMobileDevice)

    setDeviceInfo({
      isLandscape,
      isMobile: isMobileDevice && !isTabletDevice && !isUnfolded,
      isTablet: isTabletDevice || isUnfolded,
      isTouch: hasTouch
    })
  }, [])

  useEffect(() => {
    // Initial check
    checkDevice()

    // Listen to both orientation and resize events
    window.addEventListener('resize', checkDevice)
    window.addEventListener('orientationchange', checkDevice)

    // Create a media query listener for pointer capability
    const mediaQuery = window.matchMedia('(pointer: coarse)')
    mediaQuery.addEventListener('change', checkDevice)

    return () => {
      window.removeEventListener('resize', checkDevice)
      window.removeEventListener('orientationchange', checkDevice)
      mediaQuery.removeEventListener('change', checkDevice)
    }
  }, [checkDevice])

  return deviceInfo
}
